import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { listOrders, patchOrder } from "@/lib/adminApi";
import { apiErrorText, formatCents, needsPreparation, ORDER_PRODUCT, ORDER_STATUS } from "@/lib/adminHelpers";
import useNoindex from "@/lib/useNoindex";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR") : "");
const BADGE = {
  paid: "border-green-300 bg-green-50 text-green-800",
  pending: "border-amber-300 bg-amber-50 text-amber-800",
  refunded: "border-red-300 bg-red-50 text-red-800",
  failed: "border-red-300 bg-red-50 text-red-800",
  expired: "text-muted-foreground",
};

export function OrderRow({ o, onSaved }) {
  const [note, setNote] = useState(o.note || "");
  const [busy, setBusy] = useState(false);
  const prepare = needsPreparation(o);
  const save = async (body) => {
    setBusy(true);
    try {
      await patchOrder(o.id, body);
      toast.success("Kaydedildi");
      onSaved();
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally { setBusy(false); }
  };
  return (
    <TableRow data-testid="order-row" className={prepare ? "bg-amber-50" : ""}>
      <TableCell className="align-top text-xs">{fmt(o.paid_at || o.created_at)}</TableCell>
      <TableCell className="align-top text-sm">{ORDER_PRODUCT[o.product] || o.product}</TableCell>
      <TableCell className="align-top text-sm tabular-nums">{formatCents(o.amount_cents)}</TableCell>
      <TableCell className="align-top">
        <Badge variant="outline" className={BADGE[o.status] || ""}>{ORDER_STATUS[o.status] || o.status}</Badge>
        {prepare && <div className="mt-1 text-xs font-semibold text-amber-800" data-testid="order-prepare">Ödendi — rapor hazırlanacak</div>}
      </TableCell>
      <TableCell className="align-top text-xs">
        <a className="underline" href={`mailto:${encodeURIComponent(String(o.email || "").split("?")[0])}`}>{o.email}</a>
      </TableCell>
      <TableCell className="align-top text-xs">
        {o.token && <a className="underline" href={`/rapor/${o.token}`} target="_blank" rel="noreferrer">Raporu aç</a>}
      </TableCell>
      <TableCell className="align-top text-xs">{o.discount_code || "-"}</TableCell>
      <TableCell className="align-top">
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={!!o.fulfilled}
            disabled={busy}
            onCheckedChange={(v) => save({ fulfilled: v === true })}
            data-testid="order-fulfilled"
          />
          Tamamlandı
        </label>
      </TableCell>
      <TableCell className="align-top">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} placeholder="Not" className="min-w-40 text-xs" />
        <Button size="sm" className="mt-1" disabled={busy || note === (o.note || "")} onClick={() => save({ note })}>Kaydet</Button>
      </TableCell>
    </TableRow>
  );
}

export default function Orders() {
  useNoindex();
  const [status, setStatus] = useState("all");
  const [orders, setOrders] = useState(null);
  const load = useCallback(async () => {
    try {
      setOrders(await listOrders(status === "all" ? undefined : status));
    } catch (e) {
      toast.error(apiErrorText(e, "Siparişler yüklenemedi"));
    }
  }, [status]);
  useEffect(() => { setOrders(null); load(); }, [load]);

  return (
    <main className="mx-auto max-w-7xl px-4 py-6">
      <h1 className="mb-4 text-xl font-semibold">Siparişler</h1>
      <div className="mb-3 w-48">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            {Object.entries(ORDER_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {orders === null && <p className="text-sm text-muted-foreground">Yükleniyor...</p>}
      {orders && orders.length === 0 && <p className="text-sm text-muted-foreground">Sipariş yok.</p>}
      {orders && orders.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead><TableHead>Ürün</TableHead><TableHead>Tutar</TableHead><TableHead>Durum</TableHead>
                <TableHead>E-posta</TableHead><TableHead>Rapor</TableHead><TableHead>Kod</TableHead>
                <TableHead>Teslim</TableHead><TableHead>Not</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => <OrderRow key={o.id + String(o.fulfilled) + (o.note || "")} o={o} onSaved={load} />)}
            </TableBody>
          </Table>
        </div>
      )}
    </main>
  );
}
